// The video assistant.
//
// This file used to exist twice -- TheCB and TheCBAlt were byte-identical
// apart from two `left` values -- so every fix had to be made in both and the
// two skins had already drifted. Position is now a prop.
//
// House rules that shaped it: the panel is opaque (an overlay that lets the
// page through is unreadable, and no behavioural test would ever notice);
// magenta means selection and nothing else, so the launcher and DAW MODE are
// gold; and the header carries the playhead the assistant is actually reading,
// because a bot that silently knows where you are looks like a bot that
// guesses.

'use client';
import React, { useState, useEffect, useRef } from 'react';
import { Box, Button, CircularProgress, FormControl, IconButton, MenuItem, Select, TextField } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import CloseIcon from '@mui/icons-material/Close';
import ReactMarkdown from 'react-markdown';
import chatrobo from './chatrobo5.png';
import { streamVideoChat } from './videoChat';
import { formatTime } from '../ps/format';
import { surfaces, T } from '../ps/psTheme';

const DAWS = [
  'Ableton Live',
  'Apple Logic Pro',
  'Apple GarageBand',
  'Avid Pro Tools',
  'Steinberg Cubase',
  'Image-Line FL Studio',
  'PreSonus Studio One',
  'Bitwig Studio',
  'Cockos REAPER',
  'Reason Studios Reason',
];

// The signed-in watcher is stored under `userData` by Home.js. This read used
// to ask for `user`, a key nothing has ever written, so every greeting in the
// product said "Hey there".
function firstName() {
  try {
    const stored = JSON.parse(localStorage.getItem('userData'));
    return String(stored?.displayName || '').split(' ')[0] || 'there';
  } catch {
    return 'there';
  }
}

export default function TheCB({ identifier, timestamp, offsetBottom = '5%' }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [dawMode, setDawMode] = useState(false);
  const [selectedDaw, setSelectedDaw] = useState('Ableton Live');
  const [shouldAutoScroll, setShouldAutoScroll] = useState(true);

  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);

  useEffect(() => {
    const storedDaw = localStorage.getItem('selectedDaw');
    if (storedDaw) setSelectedDaw(storedDaw);
  }, []);

  // A new video is a new conversation: the transcript behind the assistant
  // just changed under it.
  useEffect(() => {
    setMessages([
      {
        role: 'assistant',
        content: `Hey ${firstName()}, how can I help you with this video?`,
      },
    ]);
  }, [identifier]);

  useEffect(() => {
    if (shouldAutoScroll) messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, shouldAutoScroll]);

  // Scrolling up to re-read something stops the log yanking itself back down.
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return undefined;

    const handleScroll = () => {
      const atBottom =
        container.scrollHeight - container.scrollTop - container.clientHeight <= 5;
      setShouldAutoScroll(atBottom);
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [isChatOpen]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!input.trim() || isLoading) return;

    const question = input;
    const history = messages.map(({ role, content }) => ({ role, content }));

    setMessages((prev) => [
      ...prev,
      { role: 'user', content: question },
      { role: 'assistant', content: '' },
    ]);
    setInput('');
    setIsLoading(true);
    setShouldAutoScroll(true);

    try {
      let full = '';
      await streamVideoChat(
        {
          identifier,
          timestamp,
          question: dawMode ? `I'm using ${selectedDaw}! ${question}` : question,
          messageHistory: history,
          userName: firstName(),
          daw: dawMode ? selectedDaw : undefined,
        },
        (chunk) => {
          full += chunk;
          setMessages((prev) => {
            const next = [...prev];
            next[next.length - 1] = { ...next[next.length - 1], content: full };
            return next;
          });
        },
      );
    } catch (error) {
      setMessages((prev) => {
        const next = [...prev];
        next[next.length - 1] = {
          role: 'assistant',
          content: 'Sorry, something went wrong reaching the assistant. Try again in a moment.',
          failed: true,
        };
        return next;
      });
    } finally {
      setIsLoading(false);
      setDawMode(false);
    }
  };

  if (!isChatOpen) {
    return (
      <button
        type="button"
        className="learn-chat__launcher"
        aria-label="Ask about this video"
        title="Ask about this video"
        onClick={() => setIsChatOpen(true)}
        style={{ bottom: offsetBottom, right: 24 }}
      >
        <img src={chatrobo} alt="" />
      </button>
    );
  }

  return (
    <Box
      role="dialog"
      aria-label="Video assistant"
      sx={{
        ...surfaces.popover,
        position: 'fixed',
        zIndex: 1260,
        bottom: { xs: '12px', sm: offsetBottom },
        right: { xs: '12px', sm: '24px' },
        left: { xs: '12px', sm: 'auto' },
        width: { xs: 'auto', sm: 420 },
        height: { xs: '62%', sm: 600 },
        maxHeight: 'calc(100vh - 24px)',
        display: 'flex',
        flexDirection: 'column',
        p: '10px',
        textAlign: 'left',
        animation: 'ps-rise 130ms cubic-bezier(0.22, 1, 0.36, 1)',
      }}
    >
      <div className="learn-chat__head">
        <img src={chatrobo} alt="" style={{ height: 20, display: 'block' }} />
        <span className="ps-label">ASK THIS VIDEO</span>
        <Box sx={{ flex: 1 }} />
        {/* The assistant answers about the moment you are watching, so the
            moment it is reading is on the label. */}
        <span className="ps-label ps-label--dim">AT</span>
        <span style={{ color: T.gold, fontSize: 12.5, fontVariantNumeric: 'tabular-nums' }}>
          {formatTime(timestamp)}
        </span>
        <IconButton size="small" aria-label="Close chat" onClick={() => setIsChatOpen(false)}>
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </div>

      <div className="learn-chat__log ps-scroll" ref={messagesContainerRef}>
        {messages.map((message, index) => {
          const streaming =
            isLoading && index === messages.length - 1 && message.role === 'assistant';
          return (
            <div
              key={index}
              className={`learn-msg learn-msg--${message.role === 'user' ? 'user' : 'bot'}`}
            >
              <div
                className="learn-msg__body learn-md"
                style={message.failed ? { borderLeftColor: T.mute, color: '#e9a495' } : undefined}
              >
                <ReactMarkdown>{message.content}</ReactMarkdown>
                {streaming && <span className="learn-msg__caret" />}
              </div>
            </div>
          );
        })}
        <div ref={messagesEndRef} />
      </div>

      <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', gap: '4px', mt: '10px' }}>
        <TextField
          fullWidth
          size="small"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Ask about this video"
          inputProps={{ 'aria-label': 'Your question' }}
          disabled={isLoading}
        />
        <Button
          type="submit"
          variant="contained"
          aria-label="Send"
          disabled={isLoading || !input.trim()}
          sx={{ minWidth: 44, px: 0 }}
        >
          {isLoading ? <CircularProgress size={15} color="inherit" /> : <SendIcon sx={{ fontSize: 17 }} />}
        </Button>
      </Box>

      <Box sx={{ display: 'flex', gap: '4px', alignItems: 'center', mt: '10px', minHeight: 30 }}>
        <Button
          variant={dawMode ? 'contained' : 'outlined'}
          onClick={() => setDawMode((prev) => !prev)}
          aria-pressed={dawMode}
          sx={{ flexShrink: 0 }}
        >
          DAW Mode
        </Button>
        {dawMode ? (
          <FormControl size="small" sx={{ flex: 1 }}>
            <Select
              value={selectedDaw}
              onChange={(e) => {
                setSelectedDaw(e.target.value);
                localStorage.setItem('selectedDaw', e.target.value);
              }}
              inputProps={{ 'aria-label': 'Your DAW' }}
              MenuProps={{ disablePortal: true, keepMounted: true }}
              sx={{ '& .MuiSelect-select': { py: '6px', fontSize: 12.5 } }}
            >
              {DAWS.map((daw) => (
                <MenuItem key={daw} value={daw}>
                  {daw}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : (
          <span style={{ fontSize: 11, lineHeight: 1.35, color: 'var(--ps-text-dim)' }}>
            Turn on for answers written for your DAW
          </span>
        )}
      </Box>
    </Box>
  );
}
